/**
 * SDK de Impresión Silenciosa para Frontend Web (React, Vue, Angular, Vite, etc.)
 * Compatible con ISI Print Agent v1.2.0+
 *
 * Seguridad: el agente solo acepta peticiones de orígenes autorizados. La primera vez que
 * tu ERP llame al agente, el cajero verá un diálogo "¿Autorizar https://tu-erp…?" junto al
 * reloj de Windows (o en el panel http://localhost:19100/). Hasta entonces las llamadas
 * fallan con code = "ORIGIN_NOT_AUTHORIZED".
 *
 * Licencia: el agente imprime solo con una licencia vigente (JWT firmado por tu backend).
 * Configure `getLicenseToken` y el SDK la entrega, la renueva antes de vencer y reintenta
 * automáticamente si el agente responde LICENSE_REQUIRED / LICENSE_EXPIRED.
 *
 * @example
 * const printer = new IsiPrinterClient({
 *   getLicenseToken: async () => (await api.post('/api/isi-print/token')).data.token,
 * });
 * await printer.printFile(pdfBlob, { printer: 'hw-usb001-a3f2' });
 */

export interface PrinterInfo {
  /** USB/Windows: ID persistente de hardware (hw-usb001-a3f2). Red: la IP (192.168.1.50:9100). Guárdelo en el ERP */
  id: string
  name: string // Nombre actual en el sistema operativo
  port: string // Puerto físico (USB001, 192.168.1.150:9100)
  driver: string // Nombre del driver
  is_default: boolean
  /** network = impresora por IP configurada en ISI Print (de la sucursal o manual) */
  type: 'spooler' | 'network' | 'cups'
  status: 'online' | 'offline' | 'paper_out' | 'paper_jam' | 'door_open' | 'error' | 'paused'
  jobs_count?: number
  /** Tipo de papel configurado en ISI Print (ausente = sin configurar) */
  paper?: PaperType
  /** Perfil completo con valores por defecto */
  profile?: PrinterProfile
  /** Nombre visible (ej. "Cocina") */
  label?: string
  /** true = impresora de red de la sucursal (viene del ERP con syncNetworkPrinters) */
  managed?: boolean
}

/** Impresora de red de la sucursal tal como la guarda el ERP */
export interface NetworkPrinter {
  /** Nombre visible, ej. "Cocina" (máx. 60) */
  name: string
  /** IP fija, ej. "192.168.1.50" o "192.168.1.50:9100" */
  address: string
  paper?: 'rollo-80' | 'rollo-58'
  chars_per_line?: number
  codepage?: 'cp850' | 'cp858' | 'cp437' | 'wpc1252' | 'ascii'
  /** Espera si otra caja está imprimiendo (5–120 s, defecto 12) */
  network_retry_seconds?: number
}

export interface SyncNetworkPrintersResult {
  added: string[]
  updated: string[]
  removed: string[]
  unchanged: string[]
}

export type PaperType = 'rollo-80' | 'rollo-58' | 'carta' | 'a4'

/** Ajustes de una impresora guardados en ISI Print */
export interface PrinterProfile {
  paper: PaperType
  /** Ancho que realmente imprime (rollo-80 = 72, rollo-58 = 48) */
  printable_width_mm?: number
  /** Columnas ESC/POS (rollo-80 = 48, rollo-58 = 32) */
  chars_per_line?: number
  codepage?: 'cp850' | 'cp858' | 'cp437' | 'wpc1252' | 'ascii' | ''
  /** "fit" (defecto) | "shrink" | "noscale" | "92%" */
  pdf_scale?: string
  /** Impresoras por IP: segundos de espera si está ocupada por otra caja o despertando (5–120, defecto 12) */
  network_retry_seconds?: number
}

export interface PrinterWarning {
  code: 'PRINTER_RENAMED' | 'PRINTER_RESOLVED_BY_ID' | string
  requested_name: string
  actual_name: string
  device_id: string
  message: string
}

export interface EscposColumn {
  text: string
  width: number // Proporción 0.0 - 1.0 (ej. 0.70 = 70%)
  align?: 'left' | 'center' | 'right'
}

export interface EscposLine {
  text?: string
  align?: 'left' | 'center' | 'right'
  bold?: boolean
  underline?: boolean
  size?: '1x' | '2x' | 'double_width' | 'double_height'
  qr?: string
  /** Columnas proporcionales; el texto largo se reparte en varias filas */
  columns?: EscposColumn[]
  /** Carácter repetido a todo el ancho del papel, ej. "-" o "=" */
  separator?: string
  /** Líneas en blanco adicionales después de esta línea */
  feed?: number
}

export interface EscposDocument {
  open_cash_drawer?: boolean
  cut?: boolean
  lines: EscposLine[]
  /** "80mm" = 48 caracteres (defecto) · "58mm" = 32 caracteres */
  paper_width?: '80mm' | '58mm'
  /** Forzar caracteres por línea (ej. 42 en algunas térmicas de 80mm) */
  chars_per_line?: number
  /** Tabla de caracteres. "cp850" (defecto) imprime tildes, ñ, ¿ y ¡ */
  codepage?: 'cp850' | 'cp858' | 'cp437' | 'wpc1252' | 'ascii'
}

/**
 * Destino de impresión. Recomendado: guardar el ID de hardware (`id` de getPrinters()),
 * que no cambia si renombran la impresora en Windows. También acepta el nombre o "IP:9100".
 * Vacío = impresora predeterminada del sistema.
 */
export interface PrintTarget {
  printer?: string
  connection?: 'spooler' | 'network'
  /**
   * Clave anti-duplicado (ej. `factura-${id}`). Si llega otra impresión con la misma clave dentro
   * de la ventana y la anterior está en cola, imprimiendo o impresa, NO se reimprime: se devuelve
   * el mismo job_id con duplicate=true. Si la anterior falló o se canceló, sí se imprime.
   */
  idempotency_key?: string
  /** Ventana de la clave en segundos (5–1800). Defecto 60 */
  idempotency_window?: number
  /** Reimpresión intencional: imprime aunque haya un duplicado reciente */
  force?: boolean
}

export type JobStatus = 'queued' | 'printing' | 'printed' | 'error' | 'cancelled'

/** Estado de un trabajo de impresión */
export interface JobInfo {
  id: string
  status: JobStatus
  /** true = ya no cambiará (impreso, cancelado o error definitivo) */
  final: boolean
  /** PAPER_OUT, OFFLINE, DOOR_OPEN, PAPER_JAM, USER_INTERVENTION, PRINTER_UNREACHABLE, CANCELLED_BY_CLIENT… */
  reason?: string
  message?: string
  printer?: string
  kind: 'escpos' | 'raw' | 'pdf' | 'image' | 'test' | 'file' | string
  channel?: 'spooler' | 'network'
  /** cancelJob puede detenerlo ahora */
  cancellable: boolean
  idempotency_key?: string
  created_at: string
  updated_at: string
}

export interface RawPrintOptions {
  copies?: number
  cut?: boolean
  open_cash_drawer?: boolean
}

/** Opciones para imprimir PDF / imágenes */
export interface FilePrintOptions {
  copies?: number // 1..99
  /** Vacío = el perfil de la impresora (o "fit"). También acepta "92%" */
  fit?: 'fit' | 'shrink' | 'noscale' | string
  /** Porcentaje del tamaño real del PDF (ej. 92). Tiene prioridad sobre fit */
  scale?: number
  /** Imprimir un PDF tamaño hoja en una impresora de rollo (reducido). Sin esto responde PAPER_MISMATCH */
  allow_paper_mismatch?: boolean
  paper?: 'letter' | 'legal' | 'A4' | 'A5' | string
  orientation?: 'portrait' | 'landscape'
  pages?: string // "1-3,5"
  monochrome?: boolean
  /** Enviar el PDF tal cual a una impresora IP:9100 que soporte PDF directo */
  passthrough?: boolean
}

export interface FilePrintResult {
  printer: string
  file_name?: string
  file_type: 'pdf' | 'image' | 'raw'
  bytes: number
  engine: string
  copies: number
  /** Ajuste aplicado: fit | shrink | noscale | 92% */
  scale?: string
  /** Tipo de papel del perfil de la impresora */
  paper?: PaperType
  page_size_mm?: { width_mm: number; height_mm: number }
  notices?: {
    code: 'PAPER_MISMATCH_FORCED' | 'ROLL_PDF_ON_SHEET' | 'PDF_SCALE_FAILED' | 'SCALE_NOT_APPLIED' | string
    message: string
  }[]
}

export interface PrintResult {
  job_id: string
  warning?: PrinterWarning
  /** Solo en impresión de archivos */
  file?: FilePrintResult
  /** Estado del trabajo al responder */
  job?: JobInfo
  /** true = no se imprimió porque es un duplicado reciente (misma idempotency_key) */
  duplicate?: boolean
}

export type LicenseCode =
  | 'LICENSE_VALID'
  | 'LICENSE_REQUIRED'
  | 'LICENSE_EXPIRED'
  | 'LICENSE_INVALID'
  | 'LICENSE_CLOCK_INVALID'
  | 'LICENSE_NOT_CONFIGURED'

export interface LicenseStatus {
  valid: boolean
  code: LicenseCode
  message: string
  licencia?: string
  comercio?: string
  expires_at?: string
  expires_in_hours?: number
}

export interface PingResult {
  /** El agente está instalado y respondió */
  online: boolean
  /** false = el cajero aún no autorizó este sitio (diálogo junto al reloj de Windows) */
  authorized?: boolean
  version?: string
  os?: string
  license?: LicenseStatus
}

/** Contenido de latest.json publicado junto a los instaladores */
export interface AgentManifest {
  version: string
  url: string
  sha256?: string
  size?: number
  min_version?: string
  notes?: string
  published_at?: string
}

/** Resultado de checkAgentUpdate() para mostrar el aviso en el ERP */
export interface AgentUpdateInfo {
  /** isi-print responde en este equipo */
  installed: boolean
  installedVersion?: string
  latestVersion: string
  /** Hay una versión más nueva publicada */
  updateAvailable: boolean
  /** La versión instalada es menor que min_version: conviene bloquear y pedir actualizar */
  updateRequired: boolean
  /** Instalador a descargar (también sirve cuando no está instalado) */
  downloadUrl: string
  notes?: string
  publishedAt?: string
}

/** Compara versiones semánticas: >0 si a es mayor, <0 si es menor, 0 si son iguales */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) =>
    v
      .replace(/^v/, '')
      .split('-')[0]
      .split('.')
      .map((n) => parseInt(n, 10) || 0)
  const pa = parse(a)
  const pb = parse(b)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

export interface ApiResponse<T = any> {
  success: boolean
  message?: string
  error?: string
  code?: string
  data?: T
  job_id?: string
  job?: JobInfo
  warning?: PrinterWarning
}

export class PrinterError extends Error {
  code?: string
  status?: number
  availablePrinters?: string[]
  suggestedMatch?: string
  data?: any

  constructor(message: string, code?: string, status?: number, data?: any) {
    super(message)
    this.name = 'PrinterError'
    this.code = code
    this.status = status
    this.data = data
    this.availablePrinters = data?.available_printers
    this.suggestedMatch = data?.suggested_match
  }
}

export interface IsiPrinterClientOptions {
  /** URL del agente. Defecto http://localhost:19100 */
  baseUrl?: string
  /**
   * Devuelve un token de licencia vigente desde SU backend (ej. POST /api/isi-print/token).
   * Si el comercio no tiene licencia, debe lanzar un error.
   */
  getLicenseToken?: () => Promise<string>
  /** Renovar la licencia cuando falten menos de estas horas para vencer. Defecto 48 */
  renewBeforeHours?: number
}

const LICENSE_RETRY_CODES = new Set(['LICENSE_REQUIRED', 'LICENSE_EXPIRED', 'LICENSE_INVALID'])

export class IsiPrinterClient {
  private baseUrl: string
  private getLicenseToken?: () => Promise<string>
  private renewBeforeHours: number
  private licensePromise: Promise<LicenseStatus> | null = null

  constructor(options: IsiPrinterClientOptions | string = {}) {
    const opts = typeof options === 'string' ? { baseUrl: options } : options
    this.baseUrl = (opts.baseUrl || 'http://localhost:19100').replace(/\/+$/, '')
    this.getLicenseToken = opts.getLicenseToken
    this.renewBeforeHours = opts.renewBeforeHours ?? 48
  }

  // ---------------------------------------------------------------- base

  /** Llama al agente; ante 402 de licencia la renueva una vez y reintenta */
  private async request<T = any>(
    path: string,
    init: RequestInit = {},
    allowLicenseRetry = true,
  ): Promise<ApiResponse<T>> {
    const res = await fetch(`${this.baseUrl}${path}`, init)
    let json: ApiResponse<T>
    try {
      json = await res.json()
    } catch {
      throw new PrinterError(
        `Respuesta inválida del agente (HTTP ${res.status})`,
        'INVALID_RESPONSE',
        res.status,
      )
    }

    if (!json.success) {
      const licenseRetry = res.status === 402 && LICENSE_RETRY_CODES.has(json.code || '')
      // Sitio aún no autorizado: si está en los dominios de la licencia, entregarla lo autoriza
      const originRetry = res.status === 403 && json.code === 'ORIGIN_NOT_AUTHORIZED'
      if ((licenseRetry || originRetry) && allowLicenseRetry && this.getLicenseToken) {
        try {
          await this.ensureLicense(true)
        } catch (e) {
          if (!originRetry) throw e
          throw new PrinterError(json.error || 'Sitio no autorizado', json.code, res.status, json.data)
        }
        return this.request<T>(path, init, false)
      }
      throw new PrinterError(json.error || 'Error del agente de impresión', json.code, res.status, json.data)
    }
    return json
  }

  private postJson<T = any>(path: string, body: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  }

  private toPrintResult(json: ApiResponse): PrintResult {
    const duplicate = json.code === 'DUPLICATE_SUPPRESSED'
    return {
      job_id: json.job_id || '',
      warning: json.warning,
      file: duplicate ? undefined : json.data,
      job: json.job,
      duplicate,
    }
  }

  // ---------------------------------------------------------------- estado

  /**
   * Detecta si isi-print está instalado (timeout 1.5 s), si este sitio está autorizado
   * y el estado de la licencia. Nunca lanza error.
   */
  async ping(timeoutMs = 1500): Promise<PingResult> {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
      const res = await fetch(`${this.baseUrl}/api/v1/ping`, { signal: controller.signal })
      clearTimeout(timeoutId)
      const json: ApiResponse = await res.json()
      if (!res.ok || !json.success) return { online: false }
      return {
        online: true,
        authorized: json.data?.authorized !== false,
        version: json.data?.version,
        os: json.data?.os,
        license: json.data?.license,
      }
    } catch {
      return { online: false }
    }
  }

  /** Diagnóstico completo del agente */
  async getStatus(): Promise<any> {
    return (await this.request('/api/v1/status')).data
  }

  // ---------------------------------------------------------------- licencia

  /** Estado de la licencia instalada en este equipo */
  async getLicense(): Promise<LicenseStatus> {
    return (await this.request<LicenseStatus>('/api/v1/license', {}, false)).data as LicenseStatus
  }

  /** Entrega al agente un token emitido por su backend */
  async installLicense(token: string): Promise<LicenseStatus> {
    const json = await this.request<LicenseStatus>(
      '/api/v1/license',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) },
      false,
    )
    return json.data as LicenseStatus
  }

  /**
   * Garantiza una licencia vigente: si no hay, venció o vence pronto, pide un token a su
   * backend (getLicenseToken) y lo instala. Llamadas simultáneas comparten la misma renovación.
   * Llamar al abrir el módulo de impresión y al iniciar sesión.
   */
  async ensureLicense(force = false): Promise<LicenseStatus> {
    if (this.licensePromise) return this.licensePromise
    this.licensePromise = (async () => {
      try {
        if (!force) {
          try {
            const current = await this.getLicense()
            if (current.valid && (current.expires_in_hours ?? 0) > this.renewBeforeHours) return current
          } catch (e) {
            // Sitio aún no autorizado: entregar la licencia (sus dominios lo autorizan)
            if (!(e instanceof PrinterError && e.code === 'ORIGIN_NOT_AUTHORIZED')) throw e
          }
        }
        if (!this.getLicenseToken) {
          throw new PrinterError('No se configuró getLicenseToken en IsiPrinterClient', 'LICENSE_REQUIRED')
        }
        const token = await this.getLicenseToken()
        return await this.installLicense(token)
      } finally {
        this.licensePromise = null
      }
    })()
    return this.licensePromise
  }

  // ---------------------------------------------------------------- impresoras

  /**
   * Una sola lista para elegir: impresoras de Windows (USB) de esta PC + impresoras de red
   * configuradas en ISI Print (las de la sucursal primero). Use `p.id` para imprimir.
   * Con `{ check: true }` se comprueba si cada impresora de red responde (status online/offline).
   */
  async getPrinters(opts: { check?: boolean } = {}): Promise<PrinterInfo[]> {
    const path = opts.check ? '/api/v1/printers?check=1' : '/api/v1/printers'
    return ((await this.request<PrinterInfo[]>(path)).data || []) as PrinterInfo[]
  }

  /**
   * Deja en ISI Print exactamente las impresoras de red de la sucursal que envía el ERP:
   * agrega las nuevas, actualiza las que cambiaron y elimina las que la sucursal ya no tiene.
   * No toca las impresoras USB ni las de red registradas a mano en el panel.
   * Llamar al iniciar sesión y después de guardar cambios en el módulo de impresoras.
   *
   * @example
   * const { data } = await api.get('/api/isi-print/impresoras');   // sucursal de la sesión
   * await printer.syncNetworkPrinters(data.data);
   */
  async syncNetworkPrinters(printers: NetworkPrinter[]): Promise<SyncNetworkPrintersResult> {
    return (await this.postJson<SyncNetworkPrintersResult>('/api/v1/printers/sync', { printers }))
      .data as SyncNetworkPrintersResult
  }

  /**
   * Tipo de papel de una impresora: sirve para pedir al backend el PDF en rollo o en carta.
   * Devuelve undefined si la impresora no está configurada en ISI Print.
   *
   * @example
   * const paper = await printer.getPrinterPaper('hw-usb001-a3f2');
   * const url = paper?.startsWith('rollo') ? factura.urlRollo : factura.urlCarta;
   */
  async getPrinterPaper(printer: string): Promise<PaperType | undefined> {
    const list = await this.getPrinters()
    const key = /^\d{1,3}(\.\d{1,3}){3}$/.test(printer.trim()) ? `${printer.trim()}:9100` : printer
    const p = list.find((x) => x.id === key || x.name.toLowerCase() === key.toLowerCase())
    if (p?.paper) return p.paper
    return (await this.getPrinterProfiles())[printer]?.paper // impresoras por IP:puerto
  }

  /** Ajustes guardados de todas las impresoras (clave: nombre, ID de hardware o IP:puerto) */
  async getPrinterProfiles(): Promise<Record<string, PrinterProfile>> {
    return (
      (await this.request<{ profiles: Record<string, PrinterProfile> }>('/api/v1/printers/profile')).data
        ?.profiles || {}
    )
  }

  /** Guarda los ajustes de una impresora; con null los elimina */
  async setPrinterProfile(
    printer: string,
    profile: PrinterProfile | null,
  ): Promise<Record<string, PrinterProfile>> {
    return (await this.postJson('/api/v1/printers/profile', { printer, profile })).data?.profiles || {}
  }

  /** Ticket ESC/POS declarativo (líneas, columnas, QR, corte, cajón) */
  async printEscpos(target: PrintTarget, doc: EscposDocument): Promise<PrintResult> {
    return this.toPrintResult(
      await this.postJson('/api/v1/print', { ...target, type: 'escpos_builder', data: doc }),
    )
  }

  /** Bytes crudos (ESC/POS, ZPL, PCL…) en base64 */
  async printRaw(
    target: PrintTarget,
    contentBase64: string,
    options?: RawPrintOptions,
  ): Promise<PrintResult> {
    return this.toPrintResult(
      await this.postJson('/api/v1/print', { ...target, type: 'raw', content: contentBase64, options }),
    )
  }

  /**
   * PDF, imagen, texto o ESC/POS binario por multipart/form-data.
   *
   * @example
   * const blob = await (await fetch('/api/facturas/5354.pdf')).blob();
   * await printer.printFile(blob, { printer: 'hw-usb001-a3f2' }, { copies: 1 }, 'factura-5354.pdf');
   */
  async printFile(
    file: Blob | File,
    target: PrintTarget = {},
    options: FilePrintOptions = {},
    filename?: string,
  ): Promise<PrintResult> {
    const form = new FormData()
    form.append('file', file, filename || (file as File).name || 'documento.pdf')
    if (target.printer) form.append('printer', target.printer)
    if (target.connection) form.append('connection', target.connection)
    if (target.idempotency_key) form.append('idempotency_key', target.idempotency_key)
    if (target.idempotency_window) form.append('idempotency_window', String(target.idempotency_window))
    if (target.force) form.append('force', 'true')
    for (const [k, v] of Object.entries(options)) {
      if (v !== undefined && v !== null && v !== '') form.append(k, String(v))
    }
    // No fijar Content-Type: el navegador agrega el boundary de multipart
    return this.toPrintResult(await this.request('/api/v1/print/file', { method: 'POST', body: form }))
  }

  /**
   * El agente descarga el PDF/imagen desde la URL y lo imprime.
   * Use `headers` si la URL requiere autenticación.
   */
  async printUrl(
    url: string,
    target: PrintTarget = {},
    options: FilePrintOptions = {},
    headers?: Record<string, string>,
  ): Promise<PrintResult> {
    return this.toPrintResult(await this.postJson('/api/v1/print/url', { url, headers, ...target, options }))
  }

  /**
   * Página de prueba: 'roll' (ticket ESC/POS con regla de columnas), 'letter' / 'a4',
   * o 'pdf' (calibración con regla en mm; `scale` prueba una escala sin guardarla, ej. "92%").
   */
  async printTestPage(
    target: PrintTarget,
    format: 'roll' | 'letter' | 'a4' | 'pdf' = 'roll',
    paperSize?: '80mm' | '58mm' | 'letter' | 'a4',
    scale?: string,
  ): Promise<PrintResult> {
    return this.toPrintResult(
      await this.postJson('/api/v1/test-page', {
        ...target,
        format,
        paper_size: paperSize || (format === 'letter' ? 'letter' : format === 'a4' ? 'a4' : ''),
        scale,
      }),
    )
  }

  /** Reimpresión intencional de un archivo (ignora la protección anti-duplicado) */
  reprintFile(
    file: Blob | File,
    target: PrintTarget = {},
    options: FilePrintOptions = {},
    filename?: string,
  ): Promise<PrintResult> {
    return this.printFile(file, { ...target, force: true }, options, filename)
  }

  /** Reimpresión intencional desde URL (ignora la protección anti-duplicado) */
  reprintUrl(
    url: string,
    target: PrintTarget = {},
    options: FilePrintOptions = {},
    headers?: Record<string, string>,
  ): Promise<PrintResult> {
    return this.printUrl(url, { ...target, force: true }, options, headers)
  }

  // ---------------------------------------------------------------- trabajos

  /** Estado de un trabajo (solo los creados por este sitio) */
  async getJob(jobId: string): Promise<JobInfo> {
    return (await this.request<JobInfo>(`/api/v1/jobs/${encodeURIComponent(jobId)}`, {}, false))
      .job as JobInfo
  }

  /** Últimos trabajos de este sitio (máx. 50, más recientes primero) */
  async listJobs(): Promise<JobInfo[]> {
    return ((await this.request<JobInfo[]>('/api/v1/jobs', {}, false)).data || []) as JobInfo[]
  }

  /**
   * Cancela solo ese trabajo. Errores posibles (PrinterError.code): JOB_ALREADY_PRINTED,
   * JOB_ALREADY_FINISHED, JOB_NOT_CANCELLABLE (ya se envía por red), JOB_NOT_FOUND.
   */
  async cancelJob(jobId: string): Promise<JobInfo> {
    return (
      await this.request<JobInfo>(
        `/api/v1/jobs/${encodeURIComponent(jobId)}/cancel`,
        { method: 'POST' },
        false,
      )
    ).job as JobInfo
  }

  /**
   * Espera a que el trabajo termine (impreso, cancelado o error definitivo) o a que pase el
   * tiempo indicado. Devuelve el último estado. Un error no definitivo (ej. PAPER_OUT) sigue
   * esperando: el trabajo saldrá al reponer el papel.
   */
  async waitForJob(
    jobId: string,
    opts: { timeoutMs?: number; intervalMs?: number; onUpdate?: (j: JobInfo) => void } = {},
  ): Promise<JobInfo> {
    const timeout = opts.timeoutMs ?? 30000
    const interval = opts.intervalMs ?? 700
    const end = Date.now() + timeout
    let last = await this.getJob(jobId)
    opts.onUpdate?.(last)
    while (!last.final && Date.now() < end) {
      await new Promise((r) => setTimeout(r, interval))
      const next = await this.getJob(jobId)
      if (next.status !== last.status || next.reason !== last.reason) opts.onUpdate?.(next)
      last = next
    }
    return last
  }

  /** Abre el cajón de dinero conectado a la impresora (solo nombre exacto o ID de hardware) */
  async openCashDrawer(printer: string, pin: 2 | 5 = 2): Promise<void> {
    await this.postJson('/api/v1/cash-drawer', { printer, pin })
  }

  /** Corta el papel (solo nombre exacto o ID de hardware) */
  async cutPaper(printer: string, mode: 'partial' | 'full' = 'partial'): Promise<void> {
    await this.postJson('/api/v1/cut', { printer, mode })
  }

  /** Purga trabajos atascados en la cola de la impresora (vacío = todas) */
  async clearQueue(printer = ''): Promise<void> {
    await this.postJson('/api/v1/queue/clear', { printer })
  }

  /**
   * Compara la versión instalada con latest.json (opción A: solo aviso).
   * Requiere que el bucket/dominio de descargas permita CORS desde su ERP.
   *
   * @example
   * const u = await printer.checkAgentUpdate('https://descargas.midominio.com/isi-print/latest.json');
   * if (!u.installed) mostrarDescarga(u.downloadUrl);
   * else if (u.updateRequired) bloquearYPedirActualizar(u);
   * else if (u.updateAvailable) mostrarAviso(u);
   */
  async checkAgentUpdate(manifestUrl: string): Promise<AgentUpdateInfo> {
    const res = await fetch(manifestUrl, { cache: 'no-store' })
    if (!res.ok)
      throw new PrinterError(
        `No se pudo leer ${manifestUrl} (HTTP ${res.status})`,
        'MANIFEST_UNAVAILABLE',
        res.status,
      )
    const m = (await res.json()) as AgentManifest
    const ping = await this.ping()

    const installed = ping.online && !!ping.version
    const current = ping.version || '0.0.0'
    return {
      installed,
      installedVersion: ping.version,
      latestVersion: m.version,
      updateAvailable: installed && compareVersions(m.version, current) > 0,
      updateRequired: installed && !!m.min_version && compareVersions(m.min_version, current) > 0,
      downloadUrl: m.url,
      notes: m.notes,
      publishedAt: m.published_at,
    }
  }

  /** El agente consulta latest.json por su cuenta (mismo resultado que el botón del panel) */
  async checkUpdate(): Promise<{
    current_version: string
    latest_version: string
    update_available: boolean
    release_notes?: string
    download_url?: string
    update_required?: boolean
  }> {
    return (await this.request('/api/v1/update/check')).data
  }
}

/**
 * Detecta el sistema operativo del navegador para sugerir el instalador correcto
 */
export function detectClientOS(): { os: 'windows' | 'macos' | 'linux'; filename: string; extension: string } {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('win')) {
    return { os: 'windows', filename: 'Setup-ISI-Print.exe', extension: '.exe' }
  }
  if (ua.includes('mac')) {
    return { os: 'macos', filename: 'isi-print-macos-arm64', extension: '' }
  }
  return { os: 'linux', filename: 'isi-print-linux-amd64', extension: '' }
}
